import { PageModel } from '@psyomics/surveyjs-core';
import React, { useCallback, useMemo, useState } from 'react';
import { SurveyModel } from 'survey-react-ui';

import { Header } from '../../../../../components/atoms/header/header';
import { Toc1, Toc2, Toc3, Toc4, Toc5 } from '../../../../../components/atoms/illustration/illustration';
import { ProgressBar, type ProgressBarItem } from '../../../../../components/molecules/progress-bar/progress-bar';
import { navigateToLastAnsweredQuestion } from '../../../utils/navigate';

import { TocCard, TocCardState } from './toc-card';
import { TocCardWrapper } from './toc-card-wrapper';
import { TocIndicator } from './toc-indicator';
import { generateTocs, TocPage } from './utils/toc-helpers';

type TocPsyomicsProps = {
  readonly survey: SurveyModel;
  readonly container: 'header' | 'left';
};

type TocRowProps = {
  readonly isFirst: boolean;
  readonly isLast: boolean;
  readonly page: TocPage;
  readonly index: number;
  readonly onClick: () => void;
};

type TocRendererProps = {
  readonly tocs: TocPage[];
};

const mobileTocIllustrations = [Toc1, Toc2, Toc3, Toc4, Toc5];
const getMobileTocIllustration = (index: number) => mobileTocIllustrations[index % mobileTocIllustrations.length];

const useRandomIllustration = (): [ReturnType<typeof getMobileTocIllustration>, () => void] => {
  const [randomIndex, setRandomIndex] = useState(Math.floor(Math.random() * mobileTocIllustrations.length));
  const Illustration = useMemo(() => getMobileTocIllustration(randomIndex), [randomIndex]);
  const updateRandomIndex = () => setRandomIndex((s) => s + 1);
  return [Illustration, updateRandomIndex];
};

const registerTocPsyomics = (props: unknown): React.JSX.Element => {
  return React.createElement(TocPsyomics, props as TocPsyomicsProps);
};

const TocRow = ({ isFirst, isLast, page, index, onClick }: TocRowProps) => {
  return (
    <TocCardWrapper isFirst={isFirst} isLast={isLast} state={page.state}>
      <div className="flex gap-2 items-center">
        {<TocIndicator state={page.state} />}
        <TocCard description={page.description} index={index} onClick={onClick} state={page.state} title={page.title} />
      </div>
    </TocCardWrapper>
  );
};

const TocRenderer = ({ tocs }: TocRendererProps) => {
  return (
    <>
      {tocs.map((page, index) => (
        <TocRow
          index={index}
          isFirst={index === 0}
          isLast={index === tocs.length - 1}
          key={`toc-row-${page.id}`}
          onClick={() => {
            page.onClick();
          }}
          page={page}
        />
      ))}
    </>
  );
};

const MobileTocRenderer = ({ tocs }: TocRendererProps) => {
  const [Illustration, updateIllustration] = useRandomIllustration();

  const handleTocClick = useCallback(
    (page: TocPage) => {
      updateIllustration();
      page.onClick();
    },
    [updateIllustration]
  );
  const progressItems = useMemo<ProgressBarItem[]>(
    () =>
      tocs.map((page) => ({
        active: page.isCurrent,
        id: page.id,
        label: page.description || page.title || '',
        onClick: page.state === 'to-do' ? undefined : () => handleTocClick(page),
        state: page.state,
      })),
    [tocs, handleTocClick]
  );

  const currentToc = useMemo(() => tocs.find(({ isCurrent }) => isCurrent), [tocs]);

  return (
    <div className="flex items-center gap-4 border-b border-b-[var(--color-neutral-200)]">
      <div className="flex-1 min-w-0">
        {currentToc ? <Header level="h5">{currentToc.title}</Header> : null}
        <ProgressBar className="px-2" items={progressItems} />
      </div>
      <Illustration className="shrink-0" width={72} />
    </div>
  );
};

const TocPsyomics = ({ survey, container }: TocPsyomicsProps) => {
  const { visiblePages, showTOC } = survey;

  const pageNavigations = useCallback(
    (page: PageModel) => {
      return (navToSectionState: TocCardState) => {
        survey.currentPage.clearErrors();
        if (navToSectionState === 'in-progress') {
          navigateToLastAnsweredQuestion(survey);
        } else {
          survey.currentPage = page;
        }
      };
    },
    [survey]
  );

  const tocs = useMemo(
    () => generateTocs(visiblePages, pageNavigations, survey.currentPage?.visibleIndex),
    [survey.currentPage?.visibleIndex, pageNavigations, visiblePages]
  );

  if (!showTOC && tocs.length) {
    if (container === 'left') {
      return (
        <div className="hidden lg:flex flex-col pt-4 px-2 h-dvh overflow-y-auto">
          <TocRenderer tocs={tocs} />
        </div>
      );
    } else if (container === 'header') {
      return (
        <div className="lg:hidden">
          <MobileTocRenderer tocs={tocs} />
        </div>
      );
    }
  }
  return null;
};

export { registerTocPsyomics, TocPsyomics };
